import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import {
  GET_PERMISSION_REGISTRY,
  CREATE_ROLE_MUTATION,
  UPDATE_ROLE_MUTATION,
  UPDATE_ROLE_PERMISSIONS_MUTATION,
  GET_ROLES,
} from '../../graphql/queries';

export interface RoleDialogRole {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  isOwnerRole?: boolean;
  permissions: string[];
}

interface RoleDialogProps {
  open: boolean;
  onClose: () => void;
  /** Mode: 'create' (empty form), 'edit' (existing role), 'clone' (prefill perms, empty name). */
  mode: 'create' | 'edit' | 'clone';
  /** The role being edited or cloned. Null in create mode. */
  role: RoleDialogRole | null;
}

interface PermissionDef {
  slug: string;
  group: string;
  label: string;
  description?: string | null;
}

export const RoleDialog: React.FC<RoleDialogProps> = ({ open, onClose, mode, role }) => {
  const isEditingSystem = mode === 'edit' && role?.isSystem === true;
  const isReadOnly = isEditingSystem;

  const { data: registryData, loading: registryLoading } = useQuery(GET_PERMISSION_REGISTRY, {
    skip: !open,
    fetchPolicy: 'cache-first',
  });

  const [createRole, { loading: creating }] = useMutation(CREATE_ROLE_MUTATION, {
    refetchQueries: [{ query: GET_ROLES }],
    awaitRefetchQueries: true,
  });
  const [updateRole, { loading: updatingMeta }] = useMutation(UPDATE_ROLE_MUTATION, {
    refetchQueries: [{ query: GET_ROLES }],
    awaitRefetchQueries: true,
  });
  const [updateRolePermissions, { loading: updatingPerms }] = useMutation(
    UPDATE_ROLE_PERMISSIONS_MUTATION,
    { refetchQueries: [{ query: GET_ROLES }], awaitRefetchQueries: true },
  );

  const saving = creating || updatingMeta || updatingPerms;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<{ name?: string; permissions?: string; submit?: string }>({});

  // Reset form whenever the dialog opens / mode/role changes.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (mode === 'create') {
      setName('');
      setDescription('');
      setSelectedPerms(new Set());
    } else if (mode === 'clone' && role) {
      setName('');
      setDescription(role.description || '');
      setSelectedPerms(new Set(role.permissions));
    } else if (mode === 'edit' && role) {
      setName(role.name);
      setDescription(role.description || '');
      setSelectedPerms(new Set(role.permissions));
    }
  }, [open, mode, role]);

  const permissions: PermissionDef[] = registryData?.permissionRegistry || [];

  // Group permissions by `group` field for nicer rendering.
  const groupedPermissions = useMemo(() => {
    const groups: Record<string, PermissionDef[]> = {};
    permissions.forEach((p) => {
      if (!groups[p.group]) groups[p.group] = [];
      groups[p.group].push(p);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, 'pl'));
  }, [permissions]);

  const togglePermission = (slug: string) => {
    if (isReadOnly) return;
    const next = new Set(selectedPerms);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    setSelectedPerms(next);
  };

  const toggleGroup = (group: string, slugs: string[]) => {
    if (isReadOnly) return;
    const allSelected = slugs.every((s) => selectedPerms.has(s));
    const next = new Set(selectedPerms);
    if (allSelected) {
      slugs.forEach((s) => next.delete(s));
    } else {
      slugs.forEach((s) => next.add(s));
    }
    setSelectedPerms(next);
  };

  const validate = (): boolean => {
    const errs: typeof errors = {};
    const trimmed = name.trim();
    if (trimmed.length < 2) errs.name = 'Nazwa musi mieć co najmniej 2 znaki';
    else if (trimmed.length > 64) errs.name = 'Nazwa nie może przekraczać 64 znaków';
    if (selectedPerms.size === 0) errs.permissions = 'Wybierz co najmniej jedno uprawnienie';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) {
      onClose();
      return;
    }
    if (!validate()) return;

    const trimmedName = name.trim();
    const trimmedDesc = description.trim() || null;
    const permsArr = Array.from(selectedPerms);

    try {
      if (mode === 'create' || mode === 'clone') {
        await createRole({
          variables: {
            data: {
              name: trimmedName,
              description: trimmedDesc,
              permissions: permsArr,
            },
          },
        });
        toast.success(`Utworzono rolę "${trimmedName}"`);
      } else if (mode === 'edit' && role) {
        const nameChanged = trimmedName !== role.name;
        const descChanged = (trimmedDesc || '') !== (role.description || '');
        const permsChanged =
          permsArr.length !== role.permissions.length ||
          permsArr.some((p) => !role.permissions.includes(p));

        if (nameChanged || descChanged) {
          await updateRole({
            variables: {
              data: {
                id: role.id,
                name: nameChanged ? trimmedName : undefined,
                description: descChanged ? trimmedDesc : undefined,
              },
            },
          });
        }
        if (permsChanged) {
          await updateRolePermissions({
            variables: { data: { id: role.id, permissions: permsArr } },
          });
        }
        toast.success(`Rola "${trimmedName}" zaktualizowana`);
      }
      onClose();
    } catch (err: any) {
      const msg = err?.message || 'Wystąpił błąd podczas zapisywania roli';
      setErrors({ ...errors, submit: msg });
      toast.error(msg);
    }
  };

  const title =
    mode === 'create' ? 'Nowa rola' : mode === 'clone' ? `Klonuj rolę: ${role?.name}` : `Edytuj rolę: ${role?.name}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[760px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="bg-background px-6 pt-6 pb-4 border-b">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Zdefiniuj nową rolę i przypisz uprawnienia'
              : mode === 'clone'
              ? 'Utwórz nową rolę z uprawnieniami skopiowanymi z roli źródłowej'
              : isReadOnly
              ? 'Rola systemowa — pola są tylko do podglądu'
              : 'Zaktualizuj nazwę, opis lub uprawnienia roli'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          <form id="role-form" onSubmit={handleSubmit} className="space-y-5">
            {isReadOnly && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 text-sm text-amber-900 dark:text-amber-200">
                <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <strong>Rola systemowa</strong> — nazwa, opis i uprawnienia są niemodyfikowalne, aby zachować spójność systemu.
                </div>
              </div>
            )}

            {errors.submit && (
              <div className="text-red-600 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                {errors.submit}
              </div>
            )}

            <div>
              <Label htmlFor="role-name">
                Nazwa <span className="text-red-500">*</span>
              </Label>
              <Input
                id="role-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="np. Kierownik zmiany"
                disabled={isReadOnly}
                maxLength={64}
                required
              />
              {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="role-description">Opis</Label>
              <Textarea
                id="role-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Opcjonalny opis roli..."
                rows={2}
                disabled={isReadOnly}
                maxLength={500}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>
                  Uprawnienia <span className="text-red-500">*</span>
                </Label>
                <span className="text-xs text-muted-foreground">
                  Wybrano: <Badge variant="outline">{selectedPerms.size}</Badge>
                </span>
              </div>
              {errors.permissions && (
                <p className="text-xs text-red-600 mb-2">{errors.permissions}</p>
              )}

              {registryLoading ? (
                <p className="text-sm text-muted-foreground">Ładowanie uprawnień...</p>
              ) : (
                <div className="space-y-4">
                  {groupedPermissions.map(([group, perms]) => {
                    const slugs = perms.map((p) => p.slug);
                    const allSelected = slugs.every((s) => selectedPerms.has(s));
                    const someSelected = slugs.some((s) => selectedPerms.has(s));
                    return (
                      <div key={group} className="border rounded-md p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-sm">{group}</h4>
                          <button
                            type="button"
                            onClick={() => toggleGroup(group, slugs)}
                            disabled={isReadOnly}
                            className="text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
                          >
                            {allSelected ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                          {perms.map((p) => (
                            <label
                              key={p.slug}
                              className={`flex items-start gap-2 text-sm p-1.5 rounded ${
                                isReadOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-accent/30'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedPerms.has(p.slug)}
                                onChange={() => togglePermission(p.slug)}
                                disabled={isReadOnly}
                                className="mt-0.5 w-4 h-4"
                              />
                              <span title={p.description || p.slug} className="select-none">
                                {p.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        {someSelected && !allSelected && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Wybrano {slugs.filter((s) => selectedPerms.has(s)).length} z {slugs.length}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </form>
        </div>

        <DialogFooter className="bg-background px-6 pb-6 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {isReadOnly ? 'Zamknij' : 'Anuluj'}
          </Button>
          {!isReadOnly && (
            <Button type="submit" form="role-form" disabled={saving}>
              {saving
                ? 'Zapisywanie...'
                : mode === 'edit'
                ? 'Zapisz zmiany'
                : mode === 'clone'
                ? 'Utwórz kopię'
                : 'Utwórz rolę'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
